import type { FocusEvent } from 'react';
import { useMemo, useRef, useState } from 'react';

import { Input } from 'design-system';
import { ZodError } from 'zod';

import { cn } from '@/lib/utils';

import type { StandardSchemaV1Issue } from '@tanstack/react-form';

export interface NameInputProps {
  /** Current controlled text value. Undefined renders as an empty string. */
  value: string | undefined;
  /** Receives the next controlled value after typing, truncated by maxLength when present. */
  onChange: (value: string) => void;
  /** Validation errors shown below the input. Accepts plain strings, Zod errors or TanStack issues. */
  error?: StandardSchemaV1Issue[] | ZodError | string;
  /** Optional focus handler forwarded to the underlying text input. */
  onFocus?: (event?: FocusEvent<HTMLInputElement>) => void;
  /** Optional blur handler. Invalid values trigger the shake animation before this callback runs. */
  onBlur?: (event?: FocusEvent<HTMLInputElement>) => void;
  /** Maximum allowed character count. Extra typed characters are ignored. */
  maxLength?: number;
  /** Override the automatic empty-value detection used for failure styling. */
  isEmpty?: boolean;
  /** Input font size in pixels. */
  size?: number;
  /** Placeholder shown when the value is empty. */
  placeholder?: string;
  /** Additional classes merged onto the underlying input, applied after the built-in styles. */
  className?: string;
}

export const NameInput = ({
  value,
  onChange,
  error,
  onFocus,
  onBlur,
  maxLength,
  isEmpty: isEmptyOverride,
  size = 14,
  placeholder,
  className
}: NameInputProps) => {
  const [shake, setShake] = useState(false);

  const isEmpty = isEmptyOverride ?? (!value || value.length === 0);
  const messages = useMemo<string[]>(() => {
    if (!error) return [];
    if (typeof error === 'string') return [error];
    if (error instanceof ZodError) {
      return error.issues.map(i => i.message);
    }
    // Fallback: assume TanStack StandardSchemaV1Issue[]
    try {
      return error.map(e => e.message);
    } catch {
      return [];
    }
  }, [error]);
  const hasErrors = messages.length > 0;

  const inputRef = useRef<HTMLInputElement | null>(null);

  // When confirming interactions happen and we are in error state, shake
  const ANIMATION_DURATION_MS = 400; // 0.4s CSS animation duration
  const SHAKE_DURATION_MS = ANIMATION_DURATION_MS + 50; // Allow a bit more than the CSS animation to ensure it completes
  const triggerShakeIfError = () => {
    if (hasErrors) {
      setShake(true);
      setTimeout(() => {
        setShake(false);
      }, SHAKE_DURATION_MS);
    }
  };

  return (
    <div className='name-input relative flex min-w-0 flex-1 items-center'>
      <Input
        aria-invalid={hasErrors}
        className={cn(
          'h-auto flex-1 border-transparent bg-gx-neutral px-1.5 py-1 focus-visible:border-gx-highlight focus-visible:ring-0',
          shake && 'animate-[export-field-shake_0.4s_ease]',
          isEmpty && 'border-gx-failure-dark bg-gx-failure-light',
          className
        )}
        maxLength={maxLength}
        placeholder={placeholder}
        ref={inputRef}
        style={{ fontSize: size }}
        value={value ?? ''}
        onBlur={event => {
          triggerShakeIfError();
          onBlur?.(event);
        }}
        onChange={event => {
          const raw = event.target.value;
          const nextVal = typeof maxLength === 'number' ? raw.slice(0, maxLength) : raw;
          onChange(nextVal);
        }}
        onFocus={event => {
          onFocus?.(event);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            triggerShakeIfError();
          }
        }}
      />

      {hasErrors && (
        <p className='error-message absolute top-full left-0 mt-1 text-xs whitespace-nowrap text-gx-failure'>
          {messages.join(', ')}
        </p>
      )}
    </div>
  );
};
